import dayjs from 'dayjs';
import type { IMaintenanceWindow } from '@zero-dash/types';
import { MAINTENANCE_TIME_FORMAT } from '@/constants/Maintenance/maintenance';
import type { MaintenancePhase } from '@/types/Maintenance/Maintenance.types';

/**
 * Resolves the short timezone label (e.g. `AEST` / `GMT+7`) for the viewer's own
 * local zone at the given instant. Locale is fixed so the naming stays in English.
 */
const timeZoneLabel = (date: Date): string => {
  const parts = new Intl.DateTimeFormat('en-AU', {
    timeZoneName: 'short',
  }).formatToParts(date);
  return parts.find((part) => part.type === 'timeZoneName')?.value ?? '';
};

/**
 * Formats a maintenance timestamp in the viewer's local time the way the design
 * shows it, e.g. `10:00 PM, Sat 28 Jul (AEST)` — each viewer sees their own zone.
 */
export const formatMaintenanceTime = (isoDate: string): string => {
  const date = dayjs(isoDate);
  if (!date.isValid()) return '';

  const label = timeZoneLabel(date.toDate());
  const formatted = date.format(MAINTENANCE_TIME_FORMAT);

  return label ? `${formatted} (${label})` : formatted;
};

export const deriveMaintenancePhase = (
  maintenanceWindow: IMaintenanceWindow | null,
  now: Date = new Date(),
): MaintenancePhase | null => {
  if (!maintenanceWindow?.isActive) return null;

  const current = dayjs(now);
  const notifyFrom = dayjs(maintenanceWindow.notifyFrom);
  const startsAt = dayjs(maintenanceWindow.startsAt);

  if (current.isBefore(notifyFrom)) return null;
  if (!current.isBefore(startsAt)) return 'active';
  if (!current.isBefore(startsAt.subtract(maintenanceWindow.reminderLeadMinutes, 'minute'))) return 'imminent';

  return 'scheduled';
};

export const isMaintenanceRunning = (
  maintenanceWindow: IMaintenanceWindow | null,
  now: Date = new Date(),
): boolean => {
  if (!maintenanceWindow?.isActive) return false;

  return !dayjs(now).isBefore(dayjs(maintenanceWindow.startsAt));
};
